import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '#environments';
import {
  AuditLogEntry,
  CancelPrCaseRequest,
  CompleteEoiRequest,
  CompleteRhwRequest,
  CpCorrespondenceItem,
  CreatePrCaseRequest,
  HistoricalCaseItem,
  PrAttachmentMetadata,
  PrCaseDetail,
  PrCaseListQuery,
  PrCasePage,
  PrCloseOutDetail,
  PrExecutionDetail,
  PrLandReviewDetail,
  PrPeReviewDetail,
  PrSchedulingGateStatus,
  PrTroubleshootingDetail,
  PrWlmReviewDetail,
  SavePrCloseOutRequest,
  SavePrEconomicsRequest,
  SavePrExecutionRequest,
  SavePrForecastProductionItem,
  SavePrLandReviewRequest,
  SavePrPeReviewRequest,
  SavePrSchedulingRequest,
  SavePrTroubleshootingRequest,
  SavePrWlmReviewRequest,
  SendBackFromExecutionRequest,
  UpdatePrDetailsRequest,
  WellviewJobTimeLogEnvelopeDto,
} from '#models';

@Injectable({
  providedIn: 'root',
})
export class PrCasesApiService {
  private _http = inject(HttpClient);
  private readonly _baseUrl = environment.apiUrl;
  private readonly _lookupUrl = `${environment.apiUrl}/lookup`;

  // ─── Lookups ──────────────────────────────────────────────────────────────

  getLookupOptions(fieldKey: string, parentValue?: string): Observable<{ value: string; displayText?: string | null }[]> {
    let params = new HttpParams();
    if (parentValue) params = params.set('parentValue', parentValue);
    return this._http.get<{ value: string; displayText?: string | null }[]>(`${this._lookupUrl}/${fieldKey}`, { params });
  }

  // ─── Case Queries ─────────────────────────────────────────────────────────

  getCases(query?: PrCaseListQuery): Observable<PrCasePage> {
    let params = new HttpParams();
    if (query?.page != null) params = params.set('page', query.page);
    if (query?.pageSize != null) params = params.set('pageSize', query.pageSize);
    if (query?.stage) params = params.set('stage', query.stage);
    if (query?.subProcess) params = params.set('subProcess', query.subProcess);
    if (query?.ownership) params = params.set('ownership', query.ownership);
    if (query?.sort) params = params.set('sort', query.sort);
    if (query?.sortDescending != null) params = params.set('sortDescending', query.sortDescending);
    if (query?.keyword) params = params.set('keyword', query.keyword);
    return this._http.get<PrCasePage>(`${this._baseUrl}/pr-cases`, { params });
  }

  getCaseById(id: number): Observable<PrCaseDetail> {
    return this._http.get<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}`);
  }

  getCaseByCaseNumber(caseNumber: string): Observable<PrCaseDetail> {
    return this._http.get<PrCaseDetail>(`${this._baseUrl}/pr-cases/by-number/${encodeURIComponent(caseNumber)}`);
  }

  // ─── Case Mutations ───────────────────────────────────────────────────────

  createCase(request: CreatePrCaseRequest): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases`, request);
  }

  cancelCase(id: number, request: CancelPrCaseRequest): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/cancel`, request);
  }

  // ─── Troubleshooting Stage ────────────────────────────────────────────────

  getHistoricalWork(id: number): Observable<HistoricalCaseItem[]> {
    return this._http.get<HistoricalCaseItem[]>(`${this._baseUrl}/pr-cases/${id}/historical-work`);
  }

  getRunTimeBetweenFailures(
    id: number,
    failureDate: Date,
    proactiveReactive: string,
  ): Observable<{ runTimeBetweenFailuresDays: number | null }> {
    const params = {
      failureDate: failureDate.toISOString(),
      proactiveReactive,
    };
    return this._http.get<{ runTimeBetweenFailuresDays: number | null }>(
      `${this._baseUrl}/pr-cases/${id}/run-time-between-failures`,
      { params },
    );
  }

  saveReviewHistoricalWork(id: number, request: SavePrTroubleshootingRequest): Observable<PrTroubleshootingDetail> {
    return this._http.put<PrTroubleshootingDetail>(`${this._baseUrl}/pr-cases/${id}/review-historical-work`, request);
  }

  updatePrDetails(id: number, request: UpdatePrDetailsRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/pr-cases/${id}/pr-details`, request);
  }

  completeReviewHistoricalWork(id: number, request: CompleteRhwRequest): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/review-historical-work/complete`, request);
  }

  saveEnterOperationalInformation(id: number, request: SavePrTroubleshootingRequest): Observable<PrTroubleshootingDetail> {
    return this._http.put<PrTroubleshootingDetail>(
      `${this._baseUrl}/pr-cases/${id}/enter-operational-information`,
      request,
    );
  }

  submitEnterOperationalInformation(id: number, request: CompleteEoiRequest): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(
      `${this._baseUrl}/pr-cases/${id}/enter-operational-information/submit`,
      request,
    );
  }

  // ─── Land Review Stage ────────────────────────────────────────────────────

  saveLandReview(id: number, request: SavePrLandReviewRequest): Observable<PrLandReviewDetail> {
    return this._http.put<PrLandReviewDetail>(`${this._baseUrl}/pr-cases/${id}/land-review`, request);
  }

  completeLandReview(id: number): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/land-review/complete`, {});
  }

  editLandDetails(id: number, request: SavePrLandReviewRequest): Observable<PrLandReviewDetail> {
    return this._http.put<PrLandReviewDetail>(`${this._baseUrl}/pr-cases/${id}/land-review/edit-details`, request);
  }

  // ─── PE Review Stage ──────────────────────────────────────────────────────

  savePeReview(id: number, request: SavePrPeReviewRequest): Observable<PrPeReviewDetail> {
    return this._http.put<PrPeReviewDetail>(`${this._baseUrl}/pr-cases/${id}/pe-review`, request);
  }

  completePeReview(id: number): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/pe-review/complete`, {});
  }

  advanceToWlm(id: number): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/advance-to-wlm`, {});
  }

  upsertForecastProduction(id: number, items: SavePrForecastProductionItem[]): Observable<PrPeReviewDetail> {
    return this._http.post<PrPeReviewDetail>(`${this._baseUrl}/pr-cases/${id}/forecast-production`, items);
  }

  upsertEconomics(id: number, request: SavePrEconomicsRequest): Observable<PrPeReviewDetail> {
    return this._http.post<PrPeReviewDetail>(`${this._baseUrl}/pr-cases/${id}/economics`, request);
  }

  // ─── Attachments ──────────────────────────────────────────────────────────

  getAttachments(id: number): Observable<PrAttachmentMetadata[]> {
    return this._http.get<PrAttachmentMetadata[]>(`${this._baseUrl}/pr-cases/${id}/attachments`);
  }

  uploadAttachment(id: number, formData: FormData): Observable<PrAttachmentMetadata> {
    return this._http.post<PrAttachmentMetadata>(`${this._baseUrl}/pr-cases/${id}/attachments`, formData);
  }

  deleteAttachment(id: number, attachmentId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/pr-cases/${id}/attachments/${attachmentId}`);
  }

  getAttachmentDownloadUrl(id: number, attachmentId: number): Observable<{ downloadUrl: string }> {
    return this._http.get<{ downloadUrl: string }>(`${this._baseUrl}/cases/${id}/attachments/${attachmentId}/download`);
  }

  getArtifactDownloadUrl(artifactType: string): Observable<{ downloadUrl: string }> {
    return this._http.get<{ downloadUrl: string }>(`${this._baseUrl}/artifacts/${encodeURIComponent(artifactType)}/download`);
  }

  // ─── Correspondence ───────────────────────────────────────────────────────

  getCorrespondence(id: number): Observable<CpCorrespondenceItem[]> {
    return this._http.get<CpCorrespondenceItem[]>(`${this._baseUrl}/pr-cases/${id}/correspondence`);
  }

  getCorrespondenceDownloadUrl(id: number, logId: number): Observable<{ downloadUrl: string }> {
    return this._http.get<{ downloadUrl: string }>(`${this._baseUrl}/pr-cases/${id}/correspondence/${logId}/download`);
  }

  setFinalDesign(id: number, attachmentId: number): Observable<PrPeReviewDetail> {
    return this._http.put<PrPeReviewDetail>(
      `${this._baseUrl}/pr-cases/${id}/attachments/${attachmentId}/final-design`,
      {},
    );
  }

  // ─── WLM Review Stage ─────────────────────────────────────────────────────
  // NOTE: No recycle method — the PR review flow has no WLM→PE recycle path (FR-022, BRU-016).

  saveWlmReview(id: number, request: SavePrWlmReviewRequest): Observable<PrWlmReviewDetail> {
    return this._http.put<PrWlmReviewDetail>(`${this._baseUrl}/pr-cases/${id}/wlm-review`, request);
  }

  submitWlmReview(id: number): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/wlm-review/submit`, {});
  }

  // ─── Scheduling Stage ─────────────────────────────────────────────────────

  getSchedulingGateStatus(id: number): Observable<PrSchedulingGateStatus> {
    return this._http.get<PrSchedulingGateStatus>(`${this._baseUrl}/pr-cases/${id}/scheduling`);
  }

  saveScheduling(id: number, request: SavePrSchedulingRequest): Observable<PrSchedulingGateStatus> {
    return this._http.put<PrSchedulingGateStatus>(`${this._baseUrl}/pr-cases/${id}/scheduling`, request);
  }

  schedule(id: number, request: SavePrSchedulingRequest): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/scheduling/schedule`, request);
  }

  sendBackFromScheduling(id: number, returnToWlmComment: string): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/scheduling/send-back`, { returnToWlmComment });
  }

  // ─── Execution Stage ──────────────────────────────────────────────────────

  getExecution(id: number): Observable<PrExecutionDetail> {
    return this._http.get<PrExecutionDetail>(`${this._baseUrl}/pr-cases/${id}/execution`);
  }

  saveExecution(id: number, request: SavePrExecutionRequest): Observable<PrExecutionDetail> {
    return this._http.put<PrExecutionDetail>(`${this._baseUrl}/pr-cases/${id}/execution`, request);
  }

  submitRigDown(id: number): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/execution/rig-down`, {});
  }

  submitExecution(id: number): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/execution/submit`, {});
  }

  sendBackFromExecution(id: number, request: SendBackFromExecutionRequest): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/execution/send-back`, request);
  }

  generateFinalReport(id: number): Observable<PrAttachmentMetadata> {
    return this._http.post<PrAttachmentMetadata>(`${this._baseUrl}/pr-cases/${id}/final-report`, {});
  }

  refreshWellView(id: number, wellViewJobId: string): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/pr-cases/${id}/refresh-wellview`, { wellViewJobId });
  }

  getWellViewTimeLogs(wellViewJobId: string): Observable<WellviewJobTimeLogEnvelopeDto> {
    return this._http.get<WellviewJobTimeLogEnvelopeDto>(
      `${this._baseUrl}/wellview-jobs/${encodeURIComponent(wellViewJobId)}/timelogs`,
    );
  }

  // ─── Close Out Stage ──────────────────────────────────────────────────────

  getCloseOut(id: number): Observable<PrCloseOutDetail | null> {
    return this._http.get<PrCloseOutDetail | null>(`${this._baseUrl}/pr-cases/${id}/close-out`);
  }

  saveCloseOut(id: number, request: SavePrCloseOutRequest): Observable<PrCloseOutDetail> {
    return this._http.put<PrCloseOutDetail>(`${this._baseUrl}/pr-cases/${id}/close-out`, request);
  }

  completeCloseOut(id: number): Observable<PrCaseDetail> {
    return this._http.post<PrCaseDetail>(`${this._baseUrl}/pr-cases/${id}/close-out/complete`, {});
  }

  reopenCloseOut(id: number): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/pr-cases/${id}/close-out/reopen`, {});
  }

  sendBackCloseOutToExecution(id: number, rationale: string | null): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/pr-cases/${id}/close-out/send-back`, { rationale });
  }

  // ─── Audit Log ──────────────────────────────────────────────────────────

  getAuditLog(id: number): Observable<AuditLogEntry[]> {
    return this._http.get<AuditLogEntry[]>(`${this._baseUrl}/pr-cases/${id}/audit-log`);
  }
}
